import { Link as RouterLink, matchPath } from "react-router-dom";
import { AvailableIntegrations } from "./components/available-integrations/AvailableIntegrations";
import { ActiveIntegrations } from "~/pages/integrations/components/active-integrations/ActiveIntegrations";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { useGetIntegrationsSummaryQuery } from "~/operations";
import { AnimatePresence } from "motion/react";
import {
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import { AddButton } from "~/components/add-button";
import {
  Home as HomeIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useCanCreate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, ScopeType, SpaceScope } from "~/hooks/useScope";

export interface IntegrationsProps {
  scope: SpaceScope | OrganizationScope;
}

export function IntegrationsPage({ scope }: IntegrationsProps) {
  const { data, error, loading } = useGetIntegrationsSummaryQuery({
    variables: { input: { spaceMrn: scope.mrn } },
    skip: scope.type !== ScopeType.Space,
  });

  const hasIntegrations = Boolean(data?.integrationsSummary?.total || 0 > 0);
  const canCreateIntegration = useCanCreate();

  const isAdding = Boolean(
    matchPath({ path: "/space/integrations/add" }, location.pathname),
  );

  setDocumentTitle(
    [isAdding ? "Add" : null, "Integrations", scope.name].flatMap(
      (crumb) => crumb ?? [],
    ),
  );

  if (error) {
    return <LoadingFailedPage />;
  }

  if (loading || !data?.integrationsSummary.integrationSummaries) {
    return <LoadingPage />;
  }

  const getTitle = () => {
    if (isAdding) {
      return !hasIntegrations
        ? "ADD YOUR FIRST INTEGRATION TO GET STARTED"
        : "AVAILABLE INTEGRATIONS";
    } else {
      return "INTEGRATIONS";
    }
  };

  const listCrumb = isAdding ? (
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={`/space/integrations?${scope.params}`}
    >
      Integrations
    </Link>
  ) : (
    <Typography key="/space/integrations">Integrations</Typography>
  );
  const addCrumb = isAdding ? (
    <Typography key="/space/integrations/add">Add</Typography>
  ) : undefined;

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    listCrumb,
    addCrumb,
  ];

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      <Grid
        container
        columnGap={2}
        mt={2}
        mb={4}
        sx={{
          alignItems: {
            xs: "start",
            sm: "center",
          },
        }}
      >
        <Grid size="grow">
          <Typography
            component="h1"
            variant="h4"
            fontWeight="bold"
            sx={{
              fontSize: {
                xs: 22,
                sm: 32,
              },
            }}
          >
            {getTitle()}
          </Typography>
        </Grid>
        <Grid sx={{ minWidth: 150 }}>
          <Box
            display="flex"
            justifyContent="end"
            sx={{
              mt: {
                xs: 0.75,
                sm: 0,
              },
            }}
          >
            {!hasIntegrations ? (
              <Button
                href="https://mondoo.com/support"
                target="_blank"
                variant="outlined"
                color="secondary"
                endIcon={<OpenInNewIcon />}
                sx={{ py: 0.5, px: 1 }}
              >
                Get Support
              </Button>
            ) : canCreateIntegration ? (
              <AddButton
                id="integration"
                aria-label="Add Integration"
                close={isAdding}
                href={
                  isAdding
                    ? `/space/integrations?${scope.params}`
                    : `/space/integrations/add?${scope.params}`
                }
              />
            ) : undefined}
          </Box>
        </Grid>
      </Grid>
      <AnimatePresence>
        {!hasIntegrations || isAdding ? (
          <AvailableIntegrations {...{ scope }} />
        ) : (
          <ActiveIntegrations
            scope={scope}
            summaries={data.integrationsSummary.integrationSummaries}
          />
        )}
      </AnimatePresence>
    </Box>
  );
}
